import { COMPONENT_TYPE, INPUT_COMPONENT_TYPE } from '@abiglobalhealth/go-types'
import { FormEvent, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { z } from 'zod'

import { Button } from '@/components/Button'
import { HEIGHT_UNIT, Input } from '@/components/Input'
import { Select } from '@/components/Select'
import { useFormErrors } from '@/hooks/useFormErrors'
import { useLoading } from '@/hooks/useLoading'
import { useUser } from '@/hooks/useUser'
import { US_STATES } from '@/utils/us-states'
import { minNumber, requiredString } from '@/utils/zod'

const zodSchema = z.object({
  address: requiredString.max(35),
  addressl2: z.string().max(35).optional(),
  city: requiredString.max(35),
  zipCode: requiredString.max(10),
  state: requiredString
})

export const DoseSpotForm = () => {
  const { t } = useTranslation()
  const { showLoading } = useLoading()
  const { user } = useUser()
  const { addFormError } = useFormErrors()
  const isUserOver19 = useMemo(
    () => user?.data?.dateOfBirth && new Date().getFullYear() - new Date(user?.data?.dateOfBirth).getFullYear() > 19,
    [user?.data?.dateOfBirth]
  )

  const handleConfirm = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault()

    const formData = new FormData(e.currentTarget)
    const formEntries = Object.fromEntries(formData.entries()) as Record<string, string>

    const heightUnit = formEntries['height-unit'] as HEIGHT_UNIT
    const heightCm = Number(formEntries['height-cm'])
    const heightFt = Number(formEntries['height-ft'])
    const heightIn = Number(formEntries['height-in'])
    const height = heightUnit === HEIGHT_UNIT.CM ? heightCm : heightFt * 12 + heightIn

    try {
      zodSchema
        .extend({
          height: isUserOver19 ? z.number().optional() : minNumber(1),
          weight: isUserOver19 ? z.number().optional() : minNumber(1)
        })
        .parse({
          address: formEntries.address,
          addressl2: formEntries.addressl2,
          city: formEntries.city,
          zipCode: formEntries.zipCode,
          state: formEntries.state,
          ...(!isUserOver19 && { height, weight: Number(formEntries.weight) })
        })
    } catch (err) {
      console.error(err)
      if (err instanceof z.ZodError) {
        for (const error of err.issues) {
          addFormError(error.path.join('.'), error.message)
        }
      } else {
        console.error(err)
      }

      return
    }

    showLoading()

    // TODO: Finish DoseSpot integration when API is ready
    throw new Error('DoseSpot integration is not finished yet')
  }

  return (
    <div className="flex flex-col gap-4 p-4">
      <p>{t('prescription.patient.address')}</p>

      <form id="user-address-form" onSubmit={handleConfirm}>
        <div className="flex flex-col pb-4">
          {!isUserOver19 && (
            <div className="flex w-full gap-2 [&>div]:w-1/2">
              <Input
                id="weight"
                componentType={COMPONENT_TYPE.INPUT}
                tabIndex={0}
                type={INPUT_COMPONENT_TYPE.WEIGHT}
                textKey="inputs.weight.label"
                placeholder="00,00"
              />
              <Input id="height" componentType={COMPONENT_TYPE.INPUT} tabIndex={1} type={INPUT_COMPONENT_TYPE.HEIGHT} textKey="inputs.height.label" />
            </div>
          )}

          <div>
            <Input
              id="address"
              tabIndex={2}
              componentType={COMPONENT_TYPE.INPUT}
              type={INPUT_COMPONENT_TYPE.TEXT}
              textKey="delivery.address.address"
              placeholder="delivery.address.address.placeholder.1"
              value={user?.data?.address}
              maxLength={35}
            />

            <Input
              id="addressl2"
              tabIndex={3}
              componentType={COMPONENT_TYPE.INPUT}
              type={INPUT_COMPONENT_TYPE.TEXT}
              placeholder="delivery.address.address.placeholder.2"
              maxLength={35}
            />
          </div>

          <Input
            id="city"
            tabIndex={4}
            componentType={COMPONENT_TYPE.INPUT}
            type={INPUT_COMPONENT_TYPE.TEXT}
            textKey="inputs.city.label"
            placeholder="inputs.city.placeholder"
            value={user?.data?.city}
            maxLength={35}
          />

          <div className="flex w-full gap-4">
            <Input
              id="zipCode"
              tabIndex={5}
              className="max-w-28"
              componentType={COMPONENT_TYPE.INPUT}
              type={INPUT_COMPONENT_TYPE.TEXT}
              textKey="delivery.address.zipcode"
              placeholder="000000"
              value={user?.data?.zipCode}
              maxLength={10}
            />

            <Select
              id="state"
              tabIndex={6}
              textKey="inputs.physicianState.label"
              className="py-1"
              options={Object.values(US_STATES).map((state) => ({
                id: state,
                textKey: `country.us.area.${state}`,
                value: state
              }))}
              value={user?.data?.state?.code}
            />
          </div>
        </div>

        <Button>{t('button.continue')}</Button>
      </form>
    </div>
  )
}
